import React, { useRef, useState } from "react";
import { Alert, Button, Col, Container, Form, Row } from "react-bootstrap";
import ReCAPTCHA from "react-google-recaptcha";
import { ContactData } from "../type/PricingData"; // Adjust the import path as needed
import { postContactUs } from "../lib/api";

const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY ?? "";

const ContactUs: React.FC = () => {
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState("");
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const handleInputChange = async (event: any) => {
    event.preventDefault();
    const form = event.target;
    const formData = new FormData(form);

    const name = formData.get("name");
    const email = formData.get("email");
    const comments = formData.get("comments");

    const validationError = validateForm(name, email, comments);
    if (validationError) {
      setErrorMsg(validationError);
      setSuccessMsg("");
      return;
    }

    if (!recaptchaToken) {
      setErrorMsg("Please confirm that you are not a robot.");
      setSuccessMsg("");
      return;
    }

    setErrorMsg("");
    setSuccessMsg("");
    setLoading(true);

    try {
      const contactData: ContactData = {
        name: name as string,
        email: email as string,
        message: comments as string,
        recaptcha_token: recaptchaToken,
      };

      await postContactUs(contactData);

      form.reset();
      setSuccessMsg("Message sent successfully! We'll get back to you soon.");
    } catch (error: any) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        setErrorMsg(error.response.data.message);
      } else {
        setErrorMsg("Failed to send message. Please try again later.");
      }
    } finally {
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
      setLoading(false);
    }
  };

  const validateForm = (name: any, email: any, comments: any) => {
    if (!name || name.trim() === "") {
      return "Please enter your name.";
    }
    if (!email || email.trim() === "") {
      return "Please enter your email.";
    }
    if (!comments || comments.trim() === "") {
      return "Please enter your comments.";
    }
    return "";
  };

  return (
    <Container>
      <Row className="justify-content-center">
        <Col lg={8}>
          <div className="home-title text-center">
            <h2 className="heading-title-title letter-spacing_4 text-uppercase">
              Contact Us
            </h2>
            <hr
              style={{
                width: "130px",
                margin: "auto",
                marginTop: "20px",
                borderTop: "1px solid #533C2B",
                opacity: 1,
              }}
            />
            <p className="heading-title-desc text-muted mt-4">
              Cherish the Moments, Connect with Us <br />
              We'd love to hear from you! Reach out to The Endless Moment for
              questions, inquiries, or custom requests.
            </p>
          </div>
        </Col>
      </Row>

      <Row className="mt-5 pt-4">
        <Col lg={12}>
          <div className="photographer-contact-form mt-4">
            <h5 className="text-uppercase f-18 letter-spacing_2">
              Get In Touch
            </h5>
          </div>
          <div className="custom-form mt-4">
            <Form onSubmit={handleInputChange} name="myForm" id="myForm">
              {errorMsg && (
                <Alert variant="danger" className="error_message">
                  {errorMsg}
                </Alert>
              )}
              {successMsg && (
                <Alert variant="success" className="success_message">
                  {successMsg}
                </Alert>
              )}
              <Row>
                <Col lg={12}>
                  <Form.Group className="my-2">
                    <Form.Control
                      name="name"
                      id="name"
                      type="text"
                      placeholder="Your Name"
                      disabled={loading}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col lg={12}>
                  <Form.Group className="my-2">
                    <Form.Control
                      name="email"
                      id="email"
                      type="email"
                      placeholder="Your Email"
                      disabled={loading}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col lg={12}>
                  <Form.Group className="my-2">
                    <Form.Control
                      name="comments"
                      id="comments"
                      as="textarea"
                      rows={5}
                      placeholder="Your Message"
                      disabled={loading}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col lg={12}>
                  <div className="my-3">
                    {recaptchaSiteKey ? (
                      <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={recaptchaSiteKey}
                        onChange={(token) => {
                          setRecaptchaToken(token);
                          if (token) {
                            setErrorMsg("");
                          }
                        }}
                        onExpired={() => setRecaptchaToken(null)}
                        onErrored={() => {
                          setRecaptchaToken(null);
                          setErrorMsg(
                            "Unable to load reCAPTCHA. Please refresh the page and try again.",
                          );
                        }}
                      />
                    ) : (
                      <Alert variant="danger" className="mb-0">
                        Contact form verification is not configured.
                      </Alert>
                    )}
                  </div>
                </Col>
              </Row>
              <Row>
                <Col lg={12}>
                  <div className="mt-2">
                    <Button
                      type="submit"
                      id="submit"
                      name="send"
                      className="submitBnt btn btn-md btn-custom"
                      disabled={loading || !recaptchaToken}
                    >
                      {loading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Sending...
                        </>
                      ) : (
                        "Send Message"
                      )}
                    </Button>
                    <div id="simple-msg"></div>
                  </div>
                </Col>
              </Row>
            </Form>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default ContactUs;
